import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Clock,
  Stethoscope,
  Paperclip,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { SoloQueuePatient } from '@/hooks/useSoloDailyQueue';

interface SoloDailyQueueListProps {
  patients: SoloQueuePatient[];
  onPatientClick: (patient: SoloQueuePatient) => void;
  onRemovePatient?: (patientId: string) => void;
  isStaffMode?: boolean;
}

export function SoloDailyQueueList({
  patients,
  onPatientClick,
  onRemovePatient,
  isStaffMode = false,
}: SoloDailyQueueListProps) {
  if (patients.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-xs">
        No patients in today's solo queue. Use "Staff Pre-Charting" to add patients.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {patients.map(p => {
        const hasDocs = p.pre_loaded_docs.length > 0;
        const verifiedCount = p.pre_loaded_docs.filter(d => d.status === 'verified').length;

        return (
          <Card
            key={p.id}
            className={cn(
              'group cursor-pointer transition-all duration-200 border-l-[3px] hover:shadow-md',
              hasDocs
                ? 'border-l-emerald-400 hover:border-l-emerald-500'
                : 'border-l-amber-300 hover:border-l-amber-400'
            )}
            onClick={() => onPatientClick(p)}
          >
            <CardContent className="p-3.5">
              <div className="flex items-start justify-between gap-3">
                {/* Left */}
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <User className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="font-semibold text-sm text-foreground">{p.patient_name}</span>
                    {p.patient_dob && (
                      <span className="text-[11px] text-muted-foreground">
                        DOB: {p.patient_dob}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(p.appointment_time), 'h:mm a')}
                    </span>
                    {p.planned_procedure && (
                      <span className="flex items-center gap-1">
                        <Stethoscope className="w-3 h-3" />
                        <span className="font-medium text-foreground truncate max-w-[200px]">{p.planned_procedure}</span>
                      </span>
                    )}
                  </div>

                  {/* Pre-loaded doc list for ready patients */}
                  {hasDocs && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      {p.pre_loaded_docs.map((doc, i) => (
                        <Badge key={i} variant="outline" className="text-[9px] bg-emerald-50/80 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                          {doc.type}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right */}
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {p.payer_name && (
                    <Badge variant="outline" className="text-[10px] font-medium bg-muted/50 text-muted-foreground border-border rounded-full">
                      {p.payer_name}
                    </Badge>
                  )}

                  {hasDocs ? (
                    <Badge variant="outline" className="text-[10px] font-semibold rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                      <Paperclip className="w-3 h-3 mr-1" />
                      {verifiedCount} Docs Pre-Loaded
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] font-medium rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Pending Clinical Docs
                    </Badge>
                  )}

                  <div className="flex items-center gap-1">
                    {isStaffMode && onRemovePatient && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onRemovePatient(p.id); }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
